import { Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { TOAST_DURATION_MS } from '@/constants/timing'
import { cn } from '@/lib/utils'
import {
  CONTACT_TITLE_LABELS,
  type ContactTitle,
} from '@/lib/schemas/contact.schema'
import type { Company } from '@/types/api'
import { AddContactModal } from '@/components/custom/contacts/add-contact-modal'
import { ContactActionsMenu } from '@/components/custom/contacts/contact-actions-menu'
import { formatContactName } from '@/lib/contacts/format-contact-name'

interface ContactsSectionProps {
  company: Company
  onUpdate?: () => void
}

export function ContactsSection({ company, onUpdate }: ContactsSectionProps) {
  const { contacts } = company
  const [isAddContactOpen, setIsAddContactOpen] = useState(false)
  const navigate = useNavigate()

  const handleSaved = () => {
    onUpdate?.()
  }

  // Cap visible contacts at 4
  const visibleContacts = contacts?.slice(0, 4) ?? []
  const hasMoreContacts = (contacts?.length ?? 0) > 4
  const isOddVisibleCount = visibleContacts.length % 2 === 1

  if (!contacts || contacts.length === 0) {
    return (
      <div className="space-y-3 border-b border-primary/10 pb-3">
        <Typography variant="h6">Company Contacts</Typography>
        <div className="rounded-md border border-primary/20 bg-white/50 p-3 dark:bg-gray-900">
          <Typography className="text-sm text-primary">
            No primary contact assigned
          </Typography>
          <Typography className="text-xs text-primary/70">
            Add a primary contact to keep communication centralized.
          </Typography>
          <Button
            type="button"
            variant="badge"
            size="badge"
            className="mt-2"
            onClick={() => setIsAddContactOpen(true)}
          >
            Add primary contact +
          </Button>
        </div>

        <AddContactModal
          open={isAddContactOpen}
          onOpenChange={setIsAddContactOpen}
          fixedCompanyId={company.id}
          forcePrimaryContact
          onSuccess={handleSaved}
        />
      </div>
    )
  }

  const handleViewAll = () => {
    navigate({ to: '/contacts' })
  }

  return (
    <div className="space-y-4 border-b border-primary/10 pb-3">
      <Typography variant="h6">Company Contacts</Typography>
      <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
        {visibleContacts.map((contact, index) => {
          const isLastCard = index === visibleContacts.length - 1
          const shouldSpanFullWidth = isLastCard && isOddVisibleCount

          return (
            <div
              key={contact.id}
              className={shouldSpanFullWidth ? 'sm:col-span-2' : ''}
            >
              <ContactCard
                contact={contact}
                companyId={company.id}
                onUpdated={handleSaved}
                showActions
              />
            </div>
          )
        })}
      </div>

      {/* Action buttons row */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="badge"
          size="badge"
          onClick={() => setIsAddContactOpen(true)}
        >
          Add new contact +
        </Button>
        {hasMoreContacts && (
          <Button
            type="button"
            variant="badge"
            size="badge"
            onClick={handleViewAll}
          >
            View all
          </Button>
        )}
      </div>

      <AddContactModal
        open={isAddContactOpen}
        onOpenChange={setIsAddContactOpen}
        fixedCompanyId={company.id}
        onSuccess={handleSaved}
      />
    </div>
  )
}

interface ContactCardProps {
  contact: {
    id: string
    firstName: string
    lastName?: string | null
    title: ContactTitle
    email?: string
    isPrimaryContact?: boolean
  }
  companyId: string
  onUpdated?: () => void
  showActions?: boolean
  className?: string
}

function ContactCard({
  contact,
  companyId,
  onUpdated,
  showActions = true,
  className,
}: ContactCardProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!contact.email) return
    await navigator.clipboard.writeText(contact.email)
    setCopied(true)
    setTimeout(() => setCopied(false), TOAST_DURATION_MS)
  }

  return (
    <div
      className={cn(
        'self-start rounded-md border bg-white/50 p-2 dark:bg-gray-900',
        contact.isPrimaryContact ? 'border-primary/50' : 'border-primary/20',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <Typography
          as="span"
          variant="body-sm"
          className="block font-medium text-primary/80"
        >
          {CONTACT_TITLE_LABELS[contact.title] ?? contact.title}
        </Typography>
        {showActions && (
          <ContactActionsMenu
            contactId={contact.id}
            contactName={formatContactName(contact)}
            companyId={companyId}
            contactTitle={contact.title}
            removeMode="remove-assignment"
            onChanged={onUpdated}
            className="h-5 w-5"
          />
        )}
      </div>
      <div className="min-w-0">
        <Typography
          as="span"
          variant="body"
          className="block font-medium text-primary"
        >
          {formatContactName(contact)}
        </Typography>
        {contact.email ? (
          <div className="flex justify-between">
            <Typography
              as="span"
              variant="body-sm"
              className="mt-0.5 block truncate text-primary/70"
            >
              {contact.email}
            </Typography>
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 shrink-0"
              onClick={handleCopy}
              aria-label={copied ? 'Email copied' : 'Copy email'}
            >
              {copied ? (
                <Check className="h-3 w-3 text-primary/20" />
              ) : (
                <Copy className="h-3 w-3 text-primary/20" />
              )}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
